import type { UpcomingPayout } from '@clarity/contract';
import { shortDate } from './format.js';

/**
 * The minimum-payout notes for the next-payout estimate (shared by the dashboard
 * balance card and the payouts panel):
 *  - `carried`: part of the amount comes from earlier rounds Talabat skipped
 *    for being under its minimum payout;
 *  - `minimum`: the total is under the minimum, so Talabat will skip this round
 *    and roll it into the following payout;
 *  - `ads`: the account-level ads already deducted from the amount (reported
 *    Talabat Ads spend + VAT, estimated where not reported, sponsored-deal fees).
 * Each is `null` when it doesn't apply.
 */
export function upcomingNotes(
  upcoming: UpcomingPayout,
  fmt: (n: number) => string,
): { carried: string | null; minimum: string | null; ads: string | null } {
  const carried =
    upcoming.carriedAmount !== null && upcoming.carriedAmount !== 0
      ? `includes ≈ ${fmt(upcoming.carriedAmount)} carried from skipped rounds`
      : null;
  const minimum =
    upcoming.belowMinimum && upcoming.minimumPayout !== null
      ? `Under Talabat’s ${fmt(upcoming.minimumPayout)} minimum, so it should roll into ${
          upcoming.rollsIntoPayoutDate ? `the ${shortDate(upcoming.rollsIntoPayoutDate)} payout` : 'the next payout'
        }`
      : null;
  const source =
    upcoming.adsSource === 'reported'
      ? 'reported by Talabat Ads'
      : upcoming.adsSource === 'mixed'
        ? 'partly estimated'
        : 'estimated';
  const ads =
    upcoming.adsDeducted !== null && upcoming.adsDeducted > 0
      ? `after ≈ ${fmt(upcoming.adsDeducted)} of ads (${source})`
      : null;
  return { carried, minimum, ads };
}
